import { DataSourceRequestState } from '@progress/kendo-data-query';
import { useQuery } from '@tanstack/react-query';

import { ExamGridModel } from 'models';

import { DataResult } from 'core/api/types/DataResult';
import { ColumnState } from 'core/ui';

import { useApiClient } from 'features/api';

export interface UsePatientExamsQueryOptions {
  patientId: number;
  dataState: DataSourceRequestState;
  columnsState?: Record<string, ColumnState> | null;
  enabled?: boolean;
  includeFiles?: boolean;
}

export function usePatientExamsQuery({ patientId, dataState, columnsState, enabled = true, includeFiles = true }: UsePatientExamsQueryOptions) {
  const apiClient = useApiClient();

  return useQuery<DataResult<ExamGridModel>>({
    queryKey: ['patient-exams', patientId, dataState, columnsState, includeFiles],
    queryFn: async ({ signal }) => {
      // Add patient filter to dataState
      const patientDataState = {
        ...dataState,
        filter: {
          logic: 'and' as const,
          filters: [
            ...(dataState.filter?.filters || []),
            {
              field: 'patientId',
              operator: 'eq' as const,
              value: patientId,
            },
          ],
        },
      };

      // Use the optimized endpoint with includeFiles parameter
      return await apiClient.exams.getAllForKendoGridOptimized(
        patientDataState,
        undefined, // quickFilter
        undefined, // groupId
        null, // worklistViewId
        includeFiles, // includeFiles
        undefined, // relativeDateField
        undefined, // relativeDateType
        'msal-required'
      );
    },
    enabled: enabled && !!patientId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
